import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Shield, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API_URL, getAuthHeaders } from "../lib/utils";
import axios from "axios";

export const ChangePassword = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validaciones
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t("pages.changePassword.allFieldsRequired"));
      return;
    }

    if (newPassword.length < 6) {
      setError(t("pages.changePassword.minLength"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("pages.changePassword.passwordsDontMatch"));
      return;
    }

    if (currentPassword === newPassword) {
      setError(t("pages.changePassword.mustBeDifferent"));
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { headers: getAuthHeaders() }
      );

      toast.success(t("pages.changePassword.success"));
      
      // Cerrar sesión y redirigir al login
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 1500);
    } catch (err) {
      const message = getErrorMessage(err, "api.errors.resetPasswordError");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <Card className="w-full max-w-md border-0 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-orange-600" />
          </div>
          <CardTitle className="font-heading text-2xl">{t("pages.changePassword.title")}</CardTitle>
          <CardDescription>
            {t("pages.changePassword.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>{t("pages.changePassword.user")}</strong> {user?.name} ({user?.email})
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="current-password">{t("pages.changePassword.currentPassword")}</Label>
              <Input
                id="current-password"
                type="password"
                placeholder={t("pages.changePassword.currentPasswordPlaceholder")}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoFocus
                data-testid="current-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-password">{t("pages.changePassword.newPassword")}</Label>
              <Input
                id="new-password"
                type="password"
                placeholder={t("pages.changePassword.newPasswordPlaceholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500">
                {t("pages.changePassword.newPasswordHint")}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t("pages.changePassword.confirmPassword")}</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder={t("pages.changePassword.confirmPasswordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="change-password-submit"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("pages.changePassword.changing")}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {t("pages.changePassword.changePassword")}
                </div>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
